import { PaginatedData, PaginatedResponse } from "../../types";
import { APIQuery } from "../../utils/sharedTypes";
import { ApiServiceCollection } from "../ApiServiceCollection.class";
import { ApiResponse } from "../RequestManager.class";
import { ApiServiceEndpoint } from "../api.types";
import { TableQuery } from "../mocks/database/MockDatabase";
import { LinkAPI } from "../mocks/database/tables/links.mockTable";
import { OrganizationLink, OrganizationLinkApi } from "./types";

export class LinkService extends ApiServiceCollection {
    protected transformMockQuery: (tableName: string, query: TableQuery) => TableQuery = (_, query) => query.map(entry => {
        if (entry.fieldName === 'organization_guid' || entry.fieldName === 'organizationGuid') {
            entry.fieldName = 'organization_id';
            entry.value = this.mockCRUD!.resolveGuidToId('organizations', entry.value);
        }
        return entry;
    });

    getLinks: ApiServiceEndpoint<PaginatedData<OrganizationLink>> = ({
        organizationGuid, skip = 0, limit = 10, query }: { skip: number; limit: number; organizationGuid: string, query?: APIQuery }
    ) => {
        const queryParams = this.endpointParamsToQueryParams({ skip, limit, query });
        const filterQueryParams = new URLSearchParams(queryParams);
        filterQueryParams.append('organization_guid', organizationGuid);

        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/organization/${organizationGuid}/link?${queryParams}`, {
            method: 'GET',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: this.getMockData({
                tableName: 'links',
                queryParams: filterQueryParams
            }),
            getMockStatus: () => this.getNextMockStatus('LinkService.getLinks'),
            transformResponsePayload: async (apiResponse: ApiResponse) => {
                const response = apiResponse.response;
                const respData = await response?.json() as PaginatedResponse<LinkAPI>;
                return {
                    success: true,
                    response: response,
                    jsonBody: {
                        total: respData.total,
                        data: (respData.links || respData.data || []).map(({
                            link_guid,
                            display_name,
                            bundle_guid,
                            bundle_display_name,
                            route_name,
                            url,
                            product
                        }) => ({
                            linkGuid: link_guid,
                            displayName: display_name,
                            bundleGuid: bundle_guid,
                            bundleName: bundle_display_name,
                            routeName: route_name,
                            url,
                            product,
                            organizationGuid
                        } as OrganizationLink))
                    }
                }
            }
        })
            .catch(error => error);
    };

    getLinkById: ApiServiceEndpoint<OrganizationLink> = (linkGuid: string) => {
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/link/${linkGuid}`, {
            method: 'GET',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: this.getMockDataSingular({
                tableName: 'links',
                queryParams: { link_guid: linkGuid }
            }),
            getMockStatus: () => this.getNextMockStatus('LinkService.getLinkById'),
            transformResponsePayload: async (apiResponse: ApiResponse) => {
                const response = apiResponse.response;
                const {
                    display_name,
                    link_guid,
                    bundle_guid,
                    route_name,
                    bundle_display_name,
                    product,
                    url
                } = await response?.json() as OrganizationLinkApi;
                return {
                    response: response as Response,
                    success: true,
                    jsonBody: {
                        displayName: display_name,
                        linkGuid: link_guid,
                        bundleGuid: bundle_guid,
                        organizationGuid: undefined,
                        routeName: route_name,
                        bundleName: bundle_display_name,
                        product, url
                    } as OrganizationLink
                }
            }
        })
            .catch(error => error);
    }

    createLink: ApiServiceEndpoint<string> = (data: Omit<OrganizationLink, 'linkGuid' | 'bundleName' | 'url'>) => {
        let response: Response | null = null;
        const reqBody: Record<string, any> = {
            organization_guid: data.organizationGuid,
            bundle_guid: data.bundleGuid,
            display_name: data.displayName,
            route_name: data.routeName,
            product: data.product === "None" ? null : data.product
        };
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/link`, {
            method: 'POST',
            headers: {
                ...this.getJSONHeaders()
            },
            body: JSON.stringify(reqBody)
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    const linkRec = this.mockCRUD!.db().getTableByName('links').createRecord(reqBody);
                    return {
                        data: linkRec.values['link_guid'],
                        status: 200
                    }
                } catch (e) {
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('LinkService.createLink')
        })
            .then(resp => {
                response = resp.response!;
                return response.text()
            })
            .then(respData => {
                return {
                    response: response as Response,
                    success: true,
                    jsonBody: respData
                }
            })
            .catch(error => error);
    }

    updateLink: ApiServiceEndpoint<void> = (
        data: Omit<OrganizationLink, "bundleName" | "url">
    ) => {
        const reqBody: Record<string, any> = {
            bundle_guid: data.bundleGuid,
            display_name: data.displayName,
            product: data.product === "None" ? null : data.product
        };
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/link/${data.linkGuid}`, {
            method: 'PATCH',
            headers: {
                ...this.getJSONHeaders()
            },
            body: JSON.stringify(reqBody)
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    this.mockCRUD!.db().getTableByName('links').updateRecords([
                        {
                            fieldName: 'link_guid',
                            value: data.linkGuid
                        }
                    ], reqBody);
                    return {
                        data: null,
                        status: 204
                    }
                } catch (e) {
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('LinkService.updateLink')
        })
            .then(response => response)
            .catch(error => error);
    };
}